import React, { useMemo, useState, useCallback, useRef } from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  RefreshControl,
  FlatList,
  StatusBar,
  Image,
} from 'react-native';
import {
  Text,
  useTheme,
  Surface,
  SegmentedButtons,
  IconButton,
} from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { Coffee, Star, Heart } from 'lucide-react-native';
import { useEquipment } from '@coffeeplexity/api-client';
import type { Equipment as SharedEquipment } from '@coffeeplexity/shared';
import { useWishlist } from '@coffeeplexity/shared';
import { EquipmentSearch } from '../components/equipment';
import { ScrollToTopFAB } from '../components/ui';

export function EquipmentScreen() {
  const theme = useTheme();
  const navigation = useNavigation<any>();
  const [searchParams, setSearchParams] = useState<any>({});
  const [activeView, setActiveView] = useState('all');
  const [refreshing, setRefreshing] = useState(false);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const flatListRef = useRef<FlatList>(null);
  const { data, isLoading, refetch, isRefetching } = useEquipment({
    page: 1,
    limit: 50,
  });
  const { toggleWishlist, isInWishlist } = useWishlist();

  const handleToggleWishlist = useCallback(
    (item: any) => {
      toggleWishlist(item.id, item);
    },
    [toggleWishlist]
  );

  const handleEquipmentPress = useCallback(
    (item: any) => {
      navigation.navigate('equipmentDetail', { equipmentId: item.id });
    },
    [navigation]
  );

  const handleParamsChange = useCallback((params: any) => {
    setSearchParams(params);
    setActiveView('all');
  }, []);

  const handleViewChange = useCallback((view: string) => {
    setActiveView(view);
    if (view === 'featured') {
      setSearchParams({ filters: { is_featured: true } });
    } else if (view === 'popular') {
      setSearchParams({ filters: { min_rating: 4.5 } });
    } else {
      setSearchParams({});
    }
  }, []);

  const handleScrollToTop = useCallback(() => {
    flatListRef.current?.scrollToOffset({ animated: true, offset: 0 });
  }, []);

  const handleScroll = useCallback((event: any) => {
    const yOffset = event.nativeEvent.contentOffset.y;
    setShowScrollToTop(yOffset > 300);
  }, []);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await refetch();
    } finally {
      setRefreshing(false);
    }
  }, [refetch]);

  const items: SharedEquipment[] = useMemo(() => data?.items || [], [data]);

  const filteredEquipment = useMemo(() => {
    return items.filter(item => {
      if (activeView === 'featured') {
        return (item as any).is_featured || (item as any).rating_average >= 4.7;
      }
      if (activeView === 'popular') {
        return (item as any).rating_average >= 4.5;
      }
      if (searchParams.search?.trim()) {
        return (
          item.name.toLowerCase().includes(searchParams.search.toLowerCase()) ||
          (item.brand || '')
            .toLowerCase()
            .includes(searchParams.search.toLowerCase()) ||
          (item.description || '')
            .toLowerCase()
            .includes(searchParams.search.toLowerCase())
        );
      }
      return true;
    });
  }, [items, activeView, searchParams]);

  const renderEquipmentItem = useCallback(
    ({ item }: { item: any }) => (
      <View style={styles.cardContainer}>
        <TouchableOpacity
          activeOpacity={0.9}
          onPress={() => handleEquipmentPress(item)}
          testID={`equipment-${item.id}`}
        >
          <Surface
            style={[
              styles.equipmentCard,
              { backgroundColor: theme.colors.surface },
            ]}
            elevation={1}
          >
            {/* Equipment Image */}
            {item.featured_image_url ? (
              <Image
                source={{ uri: item.featured_image_url }}
                style={styles.equipmentImage}
                resizeMode='cover'
                fadeDuration={0}
              />
            ) : (
              <View
                style={[
                  styles.equipmentImagePlaceholder,
                  { backgroundColor: theme.colors.surfaceVariant },
                ]}
              >
                <Coffee size={32} color={theme.colors.onSurfaceVariant} />
              </View>
            )}

            <View style={styles.equipmentContent}>
              <View style={styles.equipmentTop}>
                <View style={styles.equipmentHeader}>
                  <Text variant='labelSmall' style={styles.equipmentBrand}>
                    {item.brand || 'Brand'}
                  </Text>
                  <IconButton
                    icon={({ size }) => (
                      <Heart
                        size={size}
                        color={
                          isInWishlist(item.id)
                            ? '#ff4757'
                            : theme.colors.onSurfaceVariant
                        }
                        fill={isInWishlist(item.id) ? '#ff4757' : 'none'}
                      />
                    )}
                    size={16}
                    onPress={() => handleToggleWishlist(item)}
                    style={styles.equipmentLikeButton}
                    testID={`equipment-like-${item.id}`}
                  />
                </View>
                <Text
                  variant='titleMedium'
                  style={styles.equipmentName}
                  numberOfLines={2}
                  ellipsizeMode='tail'
                >
                  {item.name}
                </Text>
                <Text
                  variant='bodySmall'
                  style={styles.equipmentDescription}
                  numberOfLines={2}
                  ellipsizeMode='tail'
                >
                  {item.description || 'Professional coffee equipment'}
                </Text>
              </View>
              <View style={styles.equipmentFooter}>
                <Text
                  variant='labelMedium'
                  style={styles.equipmentPrice}
                  numberOfLines={1}
                  ellipsizeMode='tail'
                >
                  {item.price_min &&
                  item.price_max &&
                  item.price_min !== item.price_max
                    ? `$${item.price_min}-$${item.price_max}`
                    : item.price_min
                      ? `$${item.price_min}`
                      : 'Contact for price'}
                </Text>
                {item.rating_average ? (
                  <View style={styles.ratingContainer}>
                    <Star size={12} color='#f59e0b' fill='#f59e0b' />
                    <Text variant='labelSmall' style={styles.rating}>
                      {item.rating_average}
                    </Text>
                    <Text variant='labelSmall' style={styles.reviewCount}>
                      ({item.review_count || 0})
                    </Text>
                  </View>
                ) : (
                  <View style={styles.ratingContainer}>
                    <Star size={12} color='#d4d4d4' fill='#d4d4d4' />
                    <Text
                      variant='labelSmall'
                      style={[styles.rating, { opacity: 0.5 }]}
                    >
                      0.0
                    </Text>
                    <Text
                      variant='labelSmall'
                      style={[styles.reviewCount, { opacity: 0.5 }]}
                    >
                      (0)
                    </Text>
                  </View>
                )}
              </View>
            </View>
          </Surface>
        </TouchableOpacity>
      </View>
    ),
    [handleEquipmentPress, handleToggleWishlist, isInWishlist, theme]
  );

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
    >
      <StatusBar
        barStyle={theme.dark ? 'light-content' : 'dark-content'}
        backgroundColor='transparent'
        translucent
      />

      {/* View Toggle */}
      <View style={styles.viewToggle}>
        <SegmentedButtons
          value={activeView}
          onValueChange={handleViewChange}
          buttons={[
            { value: 'all', label: 'All' },
            { value: 'featured', label: 'Featured' },
            { value: 'popular', label: 'Popular' },
          ]}
        />
      </View>

      {/* Search */}
      {activeView === 'all' && (
        <View style={styles.searchContainer}>
          <EquipmentSearch
            onParamsChange={handleParamsChange}
            initialParams={searchParams}
          />
        </View>
      )}

      {/* Equipment List */}
      <FlatList
        ref={flatListRef}
        data={filteredEquipment}
        renderItem={renderEquipmentItem}
        keyExtractor={item => item.id}
        numColumns={2}
        columnWrapperStyle={styles.row}
        contentContainerStyle={[styles.equipmentsList, { paddingBottom: 100 }]}
        showsVerticalScrollIndicator={false}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        removeClippedSubviews={true}
        maxToRenderPerBatch={10}
        updateCellsBatchingPeriod={50}
        initialNumToRender={10}
        windowSize={10}
        refreshControl={
          <RefreshControl
            refreshing={refreshing || isRefetching}
            onRefresh={onRefresh}
            colors={[theme.colors.primary]}
            tintColor={theme.colors.primary}
          />
        }
        ListEmptyComponent={
          isLoading ? (
            <View style={styles.loadingState}>
              <Coffee size={56} color={theme.colors.onSurfaceVariant} />
              <Text
                variant='titleLarge'
                style={[styles.loadingTitle, { color: theme.colors.onSurface }]}
              >
                Loading equipment...
              </Text>
            </View>
          ) : (
            <View style={styles.emptyState}>
              <Coffee size={56} color={theme.colors.onSurfaceVariant} />
              <Text
                variant='titleLarge'
                style={[styles.emptyTitle, { color: theme.colors.onSurface }]}
              >
                No equipment found
              </Text>
              <Text
                variant='bodyMedium'
                style={[
                  styles.emptySubtitle,
                  { color: theme.colors.onSurfaceVariant },
                ]}
              >
                Try adjusting your search or filters
              </Text>
            </View>
          )
        }
      />

      {/* Scroll to Top FAB */}
      <ScrollToTopFAB
        visible={showScrollToTop}
        onPress={handleScrollToTop}
        testID='equipment-scroll-to-top'
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  animatedHeader: {
    paddingHorizontal: 0,
    overflow: 'hidden',
    zIndex: 10,
  },
  headerSurface: {
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  headerContent: {
    marginBottom: 8,
  },
  headerTitle: {
    fontWeight: 'bold',
    letterSpacing: 0.5,
  },
  headerSubtitle: {
    marginTop: 4,
    opacity: 0.8,
  },

  viewToggle: {
    paddingHorizontal: 16,
    marginBottom: 16,
    marginTop: 16,
  },
  searchContainer: {
    marginBottom: 0,
  },
  equipmentsList: {
    padding: 16,
    paddingTop: 8,
  },
  row: {
    justifyContent: 'space-between',
    paddingHorizontal: 4,
  },
  cardContainer: {
    flex: 1,
    maxWidth: '48%',
    marginBottom: 16,
    minHeight: 200,
  },
  equipmentCard: {
    borderRadius: 16,
    overflow: 'hidden',
    elevation: 1,
    height: '100%',
  },
  equipmentImage: {
    width: '100%',
    height: 100,
    backgroundColor: '#f5f5f5',
  },
  equipmentImagePlaceholder: {
    width: '100%',
    height: 100,
    justifyContent: 'center',
    alignItems: 'center',
  },
  equipmentContent: {
    padding: 12,
    flex: 1,
    justifyContent: 'space-between',
  },
  equipmentTop: {
    flex: 1,
  },
  equipmentHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  equipmentBrand: {
    opacity: 0.7,
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  equipmentLikeButton: {
    margin: 0,
    width: 24,
    height: 24,
    backgroundColor: 'transparent',
  },
  equipmentName: {
    fontWeight: '600',
    marginBottom: 6,
    lineHeight: 18,
    minHeight: 36,
    fontSize: 14,
  },
  equipmentDescription: {
    opacity: 0.7,
    marginBottom: 8,
    lineHeight: 16,
    minHeight: 32,
    flex: 1,
    fontSize: 12,
  },
  equipmentFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 8,
  },
  equipmentPrice: {
    fontWeight: '600',
    color: '#2ecc71',
    flex: 1,
    marginRight: 8,
  },
  ratingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  rating: {
    fontSize: 11,
    fontWeight: '600',
  },
  reviewCount: {
    fontSize: 11,
    fontWeight: '400',
  },
  loadingState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 80,
    paddingHorizontal: 32,
  },
  loadingTitle: {
    marginTop: 24,
    textAlign: 'center',
    fontWeight: '600',
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 80,
    paddingHorizontal: 32,
  },
  emptyTitle: {
    marginTop: 24,
    marginBottom: 12,
    textAlign: 'center',
    fontWeight: '600',
  },
  emptySubtitle: {
    textAlign: 'center',
    lineHeight: 22,
    opacity: 0.8,
  },
});
